const selectProperty = (name, property, options, defaultValue, visibility = {}) => ({
    name,
    property,
    type: 'select',
    options: options.map((id) => ({ id, label: id })),
    default: defaultValue,
    ...(visibility.requires ? { requires: visibility.requires } : {}),
    ...(visibility.requiresParent ? { requiresParent: visibility.requiresParent } : {}),
});

const textProperty = (name, property, placeholder, visibility = {}) => ({
    name,
    property,
    type: 'base',
    placeholder,
    ...(visibility.requires ? { requires: visibility.requires } : {}),
    ...(visibility.requiresParent ? { requiresParent: visibility.requiresParent } : {}),
});

const GRID_REQUIRES = { display: ['grid'] };
const GRID_PARENT_REQUIRES = { display: ['grid'] };

const addGridOptionToDisplay = (editor) => {
    const displayProp = editor.StyleManager.getProperty('general', 'display');
    if (!displayProp) return;

    const options = [...displayProp.get('options')];
    if (options.some((option) => option.id === 'grid')) return;

    const noneIndex = options.findIndex((option) => option.id === 'none');
    const gridOption = { id: 'grid', label: 'grid' };

    if (noneIndex >= 0) {
        options.splice(noneIndex, 0, gridOption);
    } else {
        options.push(gridOption);
    }

    displayProp.set('options', options);
};

export default (editor) => {
    addGridOptionToDisplay(editor);

    // --- General ---
    editor.StyleManager.addProperty('general', {
        name: 'Overflow X',
        property: 'overflow-x',
        type: 'select',
        options: [
            { id: 'visible', label: 'visible' },
            { id: 'hidden', label: 'hidden' },
            { id: 'clip', label: 'clip' },
            { id: 'scroll', label: 'scroll' },
            { id: 'auto', label: 'auto' },
        ],
        default: 'visible',
    });
    editor.StyleManager.addProperty('general', {
        name: 'Overflow Y',
        property: 'overflow-y',
        type: 'select',
        options: [
            { id: 'visible', label: 'visible' },
            { id: 'hidden', label: 'hidden' },
            { id: 'clip', label: 'clip' },
            { id: 'scroll', label: 'scroll' },
            { id: 'auto', label: 'auto' },
        ],
        default: 'visible',
    });
    editor.StyleManager.addProperty('general', {
        name: 'Z Index',
        property: 'z-index',
        type: 'integer',
        placeholder: 'auto',
    });
    editor.StyleManager.addProperty('general', {
        extend: 'cursor',
        name: 'Cursor',
        options: [
            { id: 'auto', label: 'auto' },
            { id: 'default', label: 'default' },
            { id: 'pointer', label: 'pointer' },
            { id: 'grab', label: 'grab' },
            { id: 'grabbing', label: 'grabbing' },
            { id: 'not-allowed', label: 'not-allowed' },
            { id: 'text', label: 'text' },
            { id: 'move', label: 'move' },
            { id: 'help', label: 'help' },
            { id: 'crosshair', label: 'crosshair' },
            { id: 'copy', label: 'copy' },
        ],
    });
    editor.StyleManager.addProperty(
        'general',
        selectProperty('Visibility', 'visibility', ['visible', 'hidden', 'collapse'], 'visible')
    );
    editor.StyleManager.addProperty(
        'general',
        selectProperty('Pointer Events', 'pointer-events', ['auto', 'none'], 'auto')
    );
    editor.StyleManager.addProperty(
        'general',
        selectProperty('User Select', 'user-select', ['auto', 'none', 'text', 'all'], 'auto')
    );

    // --- Dimension (insert before margin/padding so all size props stay grouped) ---
    editor.StyleManager.addProperty('dimension', 'min-width', { at: 2 });
    editor.StyleManager.addProperty('dimension', 'max-height', { at: 5 });
    editor.StyleManager.addProperty(
        'dimension',
        selectProperty('Box Sizing', 'box-sizing', ['content-box', 'border-box'], 'content-box')
    );
    editor.StyleManager.addProperty(
        'dimension',
        {
            name: 'Aspect Ratio',
            property: 'aspect-ratio',
            type: 'base',
            placeholder: 'e.g. 16 / 9',
        }
    );

    // --- Flex ---
    const flexItemProperties = ['flex-grow', 'flex-basis', 'align-self'];
    flexItemProperties.forEach((property) => {
        if (editor.StyleManager.getProperty('flex', property)) {
            editor.StyleManager.removeProperty('flex', property);
        }
    });

    editor.StyleManager.addProperty(
        'flex',
        {
            name: 'Gap',
            property: 'gap',
            type: 'number',
            units: ['px', 'rem', 'em', '%'],
            default: '0',
            min: 0,
            requires: { display: ['flex'] },
        },
        { at: 2 }
    );
    editor.StyleManager.addProperty('flex', { extend: 'flex-grow' }, { at: 7 });
    editor.StyleManager.addProperty('flex', { extend: 'flex-basis' }, { at: 8 });
    editor.StyleManager.addProperty('flex', { extend: 'align-self' }, { at: 9 });

    // --- Grid ---
    editor.StyleManager.addSector(
        'grid',
        {
            name: 'Grid',
            open: false,
            properties: [
                {
                    name: 'Gap',
                    property: 'gap',
                    type: 'number',
                    units: ['px', 'rem', 'em', '%'],
                    default: '0',
                    min: 0,
                    requires: GRID_REQUIRES,
                },
                {
                    name: 'Row Gap',
                    property: 'row-gap',
                    type: 'number',
                    units: ['px', 'rem', 'em', '%'],
                    default: '0',
                    min: 0,
                    requires: GRID_REQUIRES,
                },
                {
                    name: 'Column Gap',
                    property: 'column-gap',
                    type: 'number',
                    units: ['px', 'rem', 'em', '%'],
                    default: '0',
                    min: 0,
                    requires: GRID_REQUIRES,
                },
                textProperty(
                    'Grid Template Columns',
                    'grid-template-columns',
                    'e.g. repeat(3, 1fr)',
                    { requires: GRID_REQUIRES }
                ),
                textProperty(
                    'Grid Template Rows',
                    'grid-template-rows',
                    'e.g. auto 1fr auto',
                    { requires: GRID_REQUIRES }
                ),
                textProperty(
                    'Grid Template Areas',
                    'grid-template-areas',
                    'e.g. "header header" "sidebar main"',
                    { requires: GRID_REQUIRES }
                ),
                {
                    name: 'Grid Auto Flow',
                    property: 'grid-auto-flow',
                    type: 'select',
                    default: 'row',
                    options: [
                        { id: 'row', label: 'row' },
                        { id: 'column', label: 'column' },
                        { id: 'row dense', label: 'row dense' },
                        { id: 'column dense', label: 'column dense' },
                    ],
                    requires: GRID_REQUIRES,
                },
                textProperty('Grid Auto Columns', 'grid-auto-columns', 'e.g. minmax(100px, auto)', {
                    requires: GRID_REQUIRES,
                }),
                textProperty('Grid Auto Rows', 'grid-auto-rows', 'e.g. minmax(100px, auto)', {
                    requires: GRID_REQUIRES,
                }),
                selectProperty(
                    'Justify Items',
                    'justify-items',
                    ['normal', 'stretch', 'center', 'start', 'end'],
                    'normal',
                    { requires: GRID_REQUIRES }
                ),
                selectProperty(
                    'Align Items',
                    'align-items',
                    ['normal', 'stretch', 'center', 'start', 'end'],
                    'normal',
                    { requires: GRID_REQUIRES }
                ),
                selectProperty(
                    'Justify Content',
                    'justify-content',
                    [
                        'normal',
                        'center',
                        'start',
                        'end',
                        'stretch',
                        'space-around',
                        'space-between',
                        'space-evenly',
                    ],
                    'normal',
                    { requires: GRID_REQUIRES }
                ),
                selectProperty(
                    'Align Content',
                    'align-content',
                    [
                        'normal',
                        'center',
                        'start',
                        'end',
                        'stretch',
                        'space-around',
                        'space-between',
                        'space-evenly',
                    ],
                    'normal',
                    { requires: GRID_REQUIRES }
                ),
                textProperty('Grid Column', 'grid-column', 'e.g. 1 / 3', {
                    requiresParent: GRID_PARENT_REQUIRES,
                }),
                textProperty('Grid Row', 'grid-row', 'e.g. 1 / 2', {
                    requiresParent: GRID_PARENT_REQUIRES,
                }),
                textProperty('Grid Column Start', 'grid-column-start', 'e.g. 1', {
                    requiresParent: GRID_PARENT_REQUIRES,
                }),
                textProperty('Grid Column End', 'grid-column-end', 'e.g. 3', {
                    requiresParent: GRID_PARENT_REQUIRES,
                }),
                textProperty('Grid Row Start', 'grid-row-start', 'e.g. 1', {
                    requiresParent: GRID_PARENT_REQUIRES,
                }),
                textProperty('Grid Row End', 'grid-row-end', 'e.g. 2', {
                    requiresParent: GRID_PARENT_REQUIRES,
                }),
                textProperty('Grid Area', 'grid-area', 'e.g. header', {
                    requiresParent: GRID_PARENT_REQUIRES,
                }),
                selectProperty(
                    'Justify Self',
                    'justify-self',
                    ['auto', 'normal', 'stretch', 'center', 'start', 'end'],
                    'auto',
                    { requiresParent: GRID_PARENT_REQUIRES }
                ),
                selectProperty(
                    'Align Self',
                    'align-self',
                    ['auto', 'normal', 'stretch', 'center', 'start', 'end'],
                    'auto',
                    { requiresParent: GRID_PARENT_REQUIRES }
                ),
            ],
        },
        { at: 2 }
    );

    // --- Typography ---
    editor.StyleManager.addProperty('typography', {
        name: 'Hyphens',
        property: 'hyphens',
        type: 'select',
        options: [
            { id: 'none', label: 'none' },
            { id: 'auto', label: 'auto' },
        ],
        default: 'none',
    });
    editor.StyleManager.addProperty('typography', {
        name: 'Text Decoration',
        property: 'text-decoration',
        type: 'composite',
        properties: [
            {
                name: 'Line',
                property: 'text-decoration-line',
                type: 'select',
                default: 'none',
                options: [
                    { id: 'none', label: 'none' },
                    { id: 'underline', label: 'underline' },
                    { id: 'line-through', label: 'line-through' },
                    { id: 'overline', label: 'overline' },
                ],
            },
            {
                name: 'Style',
                property: 'text-decoration-style',
                type: 'select',
                default: 'solid',
                options: [
                    { id: 'solid', label: 'solid' },
                    { id: 'double', label: 'double' },
                    { id: 'dotted', label: 'dotted' },
                    { id: 'dashed', label: 'dashed' },
                    { id: 'wavy', label: 'wavy' },
                ],
            },
            {
                name: 'Color',
                property: 'text-decoration-color',
                type: 'color',
            },
        ],
    });
    editor.StyleManager.addProperty(
        'typography',
        selectProperty(
            'Text Transform',
            'text-transform',
            ['none', 'uppercase', 'lowercase', 'capitalize'],
            'none'
        )
    );
    editor.StyleManager.addProperty(
        'typography',
        selectProperty('Font Style', 'font-style', ['normal', 'italic', 'oblique'], 'normal')
    );
    editor.StyleManager.addProperty(
        'typography',
        selectProperty(
            'White Space',
            'white-space',
            ['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces'],
            'normal'
        )
    );
    editor.StyleManager.addProperty(
        'typography',
        selectProperty('Text Overflow', 'text-overflow', ['clip', 'ellipsis'], 'clip')
    );
    editor.StyleManager.addProperty(
        'typography',
        selectProperty(
            'Word Break',
            'word-break',
            ['normal', 'break-all', 'keep-all', 'break-word'],
            'normal'
        )
    );
    editor.StyleManager.addProperty(
        'typography',
        selectProperty(
            'Overflow Wrap',
            'overflow-wrap',
            ['normal', 'break-word', 'anywhere'],
            'normal'
        )
    );
    editor.StyleManager.addProperty(
        'typography',
        selectProperty(
            'Font Variant',
            'font-variant',
            ['normal', 'small-caps', 'all-small-caps', 'petite-caps', 'all-petite-caps', 'unicase', 'titling-caps'],
            'normal'
        )
    );

    // --- Decorations ---
    editor.StyleManager.addProperty('decorations', {
        name: 'Outline',
        property: 'outline',
        type: 'composite',
        properties: [
            {
                name: 'Width',
                property: 'outline-width',
                type: 'number',
                units: ['px'],
                default: '0',
                min: 0,
            },
            {
                name: 'Style',
                property: 'outline-style',
                type: 'select',
                default: 'none',
                options: [
                    { id: 'none', label: 'none' },
                    { id: 'solid', label: 'solid' },
                    { id: 'dashed', label: 'dashed' },
                    { id: 'dotted', label: 'dotted' },
                    { id: 'double', label: 'double' },
                ],
            },
            {
                name: 'Color',
                property: 'outline-color',
                type: 'color',
                default: 'black',
            },
        ],
    });
    editor.StyleManager.addProperty('decorations', {
        name: 'Outline Offset',
        property: 'outline-offset',
        type: 'number',
        units: ['px'],
        default: '0',
    });
    editor.StyleManager.addProperty('decorations', {
        name: 'Filter',
        property: 'filter',
        type: 'base',
        placeholder: 'e.g. blur(4px) brightness(1.2)',
    });
    editor.StyleManager.addProperty('decorations', {
        name: 'Backdrop Filter',
        property: 'backdrop-filter',
        type: 'base',
        placeholder: 'e.g. blur(8px)',
    });
};
